@file:OptIn(ExperimentalComposeWebApi::class)

package org.example.project.screens_sys

import androidx.compose.runtime.*
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.example.project.dashboard.DashboardTheme
import org.example.project.dashboard.POPUP_ENGINE_MODAL_Z_INDEX
import org.example.project.dashboard.PopupEngineCard
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import kotlinx.serialization.json.put
import org.example.project.cleaning.SharedWorkspaceStyle
import org.example.project.enginecardview.layouts.TorCardStyles
import org.example.project.manager.SystemNodeError
import org.example.project.manager.SystemNodeInfo
import org.example.project.service.LocalScreenInternalTitleVisible
import org.example.project.service.UserSettingsApi
import org.example.project.settings.LumaticaButton
import org.example.project.settings.LumaticaButtonVariant
import org.example.project.settings.UISettings
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.ExperimentalComposeWebApi
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

private const val NODE_KONTROLL_COMMENTS_FILE = "node_kontrollcenter_comments.json"

@Composable
fun ServerUebersicht() {
    Style(TorCardStyles)
    Style(cssRules = SharedWorkspaceStyle.cssRules)
    val scope = rememberCoroutineScope()
    val internalTitleVisible = LocalScreenInternalTitleVisible.current
    var nodes by remember { mutableStateOf<List<SystemNodeInfo>>(emptyList()) }
    var selectedNodeId by remember { mutableStateOf<String?>(null) }
    var selectedErrors by remember { mutableStateOf<List<SystemNodeError>>(emptyList()) }
    var errorPopupNodeId by remember { mutableStateOf<String?>(null) }
    var loadingNodes by remember { mutableStateOf(true) }
    var loadingErrors by remember { mutableStateOf(false) }
    var actionBusyNodes by remember { mutableStateOf<Set<String>>(emptySet()) }
    var runtimeTickSeconds by remember { mutableStateOf(0) }
    var nodeComments by remember { mutableStateOf<Map<String, String>>(emptyMap()) }
    var companyNames by remember { mutableStateOf<Map<String, String>>(emptyMap()) }

    suspend fun refreshNodes() {
        val loaded = NodeUebersichtApi.loadNodes()
            .sortedWith(compareBy<SystemNodeInfo> { typeOrder(it.typ) }.thenBy { it.firmenId })
        nodes = loaded
        runtimeTickSeconds = 0
        if (selectedNodeId == null && loaded.isNotEmpty()) selectedNodeId = loaded.first().id
        if (selectedNodeId != null && loaded.none { it.id == selectedNodeId }) {
            selectedNodeId = loaded.firstOrNull()?.id
        }
    }

    suspend fun loadErrors(nodeId: String?) {
        if (nodeId.isNullOrBlank()) {
            selectedErrors = emptyList()
            return
        }
        loadingErrors = true
        selectedErrors = NodeUebersichtApi.loadNodeErrors(nodeId, 200)
        loadingErrors = false
    }

    LaunchedEffect(Unit) {
        refreshNodes()
        loadingNodes = false
        runtimeTickSeconds = 0
        nodeComments = parseNodeComments(UserSettingsApi.loadCurrentUserSettingsFile(NODE_KONTROLL_COMMENTS_FILE))
        companyNames = FirmenregisterApi.loadCompanies()
            .associate { (it.firmenId.ifBlank { it.id }) to it.firma.ifBlank { it.kunde } }
    }

    LaunchedEffect(Unit) {
        while (true) {
            delay(1000)
            runtimeTickSeconds++
        }
    }

    LaunchedEffect(Unit) {
        while (true) {
            delay(5000)
            refreshNodes()
        }
    }

    val errorPopupNode = nodes.firstOrNull { it.id == errorPopupNodeId }
    val selectedNode = nodes.firstOrNull { it.id == selectedNodeId }

    fun triggerSelectedAction(action: String) {
        val node = selectedNode ?: return
        if (node.id in actionBusyNodes) return
        actionBusyNodes = actionBusyNodes + node.id
        nodes = nodes.map { current ->
            if (current.id != node.id) current
            else when (action) {
                "stop" -> current.copy(status = "OFFLINE", processId = "", laufzeit = "")
                "start", "restart" -> current.copy(status = "STARTING")
                else -> current
            }
        }
        scope.launch {
            NodeUebersichtApi.triggerNodeAction(node.id, action)
            refreshNodes()
            actionBusyNodes = actionBusyNodes - node.id
        }
    }

    fun openSelectedErrors() {
        val node = selectedNode ?: return
        errorPopupNodeId = node.id
        scope.launch { loadErrors(node.id) }
    }

    Div({
        style {
            position(Position.Relative)
            height(100.percent)
        }
    }) {
        Div({ classes(SharedWorkspaceStyle.workspace) }) {
            Div({ classes(SharedWorkspaceStyle.side, SharedWorkspaceStyle.content); style { width(100.percent) } }) {
                Div({
                    classes(SharedWorkspaceStyle.contentInner)
                    style {
                        maxWidth(100.percent)
                        flex(1)
                        minHeight(0.px)
                        overflow("hidden")
                    }
                }) {
                    if (internalTitleVisible) {
                        Div({ classes(SharedWorkspaceStyle.rightTitle) }) {
                            Span({ classes(SharedWorkspaceStyle.sectionTitle) }) { Text("Serverübersicht") }
                        }
                    }

                    Div({
                        classes(SharedWorkspaceStyle.actionBar)
                        style {
                            gridTemplateColumns("repeat(4, minmax(0, 160px))")
                            maxWidth(760.px)
                            padding(12.px)
                        }
                    }) {
                        LumaticaButton(
                            label = "Start",
                            onClick = { triggerSelectedAction("start") },
                            variant = LumaticaButtonVariant.Standard,
                            enabled = selectedNode?.steuerbar == true && selectedNode.id !in actionBusyNodes,
                            heightPx = 42
                        )
                        LumaticaButton(
                            label = "Stop",
                            onClick = { triggerSelectedAction("stop") },
                            variant = LumaticaButtonVariant.Standard,
                            enabled = selectedNode?.steuerbar == true && selectedNode.id !in actionBusyNodes,
                            heightPx = 42
                        )
                        LumaticaButton(
                            label = "Reset",
                            onClick = { triggerSelectedAction("restart") },
                            variant = LumaticaButtonVariant.Standard,
                            enabled = selectedNode?.steuerbar == true && selectedNode.id !in actionBusyNodes,
                            heightPx = 42
                        )
                        LumaticaButton(
                            label = "Fehler",
                            onClick = { openSelectedErrors() },
                            variant = LumaticaButtonVariant.Standard,
                            enabled = selectedNode != null,
                            heightPx = 42
                        )
                    }

                    Div({ classes(SharedWorkspaceStyle.centeredDivider) })

                    Div({
                        style {
                            width(100.percent)
                            flexGrow(1)
                            minHeight(0.px)
                            overflowY("auto")
                            paddingBottom(18.px)
                            boxSizing("border-box")
                        }
                    }) {
                        if (loadingNodes) {
                            Div({
                                style {
                                    color(DashboardTheme.TEXT_LIGHT)
                                    fontSize(13.px)
                                    textAlign("center")
                                }
                            }) { Text("Lade Node Status...") }
                        } else {
                            Div({
                                style {
                                    display(DisplayStyle.Grid)
                                    gridTemplateColumns("repeat(auto-fit, minmax(360px, 360px))")
                                    justifyContent(JustifyContent.Center)
                                    gap(12.px)
                                    alignContent(AlignContent.Start)
                                    alignItems(AlignItems.Start)
                                    width(100.percent)
                                }
                            }) {
                                nodes.forEach { node ->
                                    NodeKontrollCard(
                                        node = node,
                                        selected = node.id == selectedNodeId,
                                        comment = nodeComments[node.id].orEmpty(),
                                        companyName = nodeCompanyName(node, companyNames),
                                        runtimeTickSeconds = runtimeTickSeconds,
                                        onSelect = { selectedNodeId = node.id },
                                        onCommentSave = { text ->
                                            val next = nodeComments + (node.id to text)
                                            nodeComments = next
                                            scope.launch {
                                                UserSettingsApi.saveCurrentUserSettingsFile(
                                                    NODE_KONTROLL_COMMENTS_FILE,
                                                    nodeCommentsJson(next)
                                                )
                                            }
                                        }
                                    )
                                }
                            }
                        }
                    }
                }
            }
        }

        if (errorPopupNode != null) {
            PopupEngineCard(
                windowKey = "node-errors-${errorPopupNode.id}",
                zIndex = POPUP_ENGINE_MODAL_Z_INDEX,
                widthPx = 520,
                heightPx = 620,
                initialX = 420.0,
                initialY = 110.0,
                onClose = {
                    errorPopupNodeId = null
                    selectedErrors = emptyList()
                }
            ) {
                Div({
                    style {
                        flexGrow(1)
                        minHeight(0.px)
                        padding(14.px)
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(10.px)
                        overflow("hidden")
                        property("box-sizing", "border-box")
                        property("background", "linear-gradient(160deg, rgba(255,255,255,0.045) 0%, rgba(0,0,0,0.18) 100%)")
                    }
                }) {
                    Row("Typ", errorPopupNode.typ)
                    Row("Umgebung", errorPopupNode.environment.uppercase().ifBlank { "-" })
                    Row("Port", errorPopupNode.port)
                    Row("Version", errorPopupNode.version.ifBlank { "-" })
                    Row("Startzeit", errorPopupNode.startzeit.ifBlank { "-" })
                    Row("Datenpfad", errorPopupNode.dataPath.ifBlank { "-" })
                    Row("Logpfad", errorPopupNode.logPath.ifBlank { "-" })
                    Row("Prozess-ID", errorPopupNode.processId.ifBlank { "-" })
                    Row("Fehler", errorPopupNode.fehlerCounter.toString())
                    Row("Status", errorPopupNode.status)
                    if (errorPopupNode.typ == "CUS" || errorPopupNode.typ == "NET" || errorPopupNode.typ == "SYS") {
                        Row("Kunde", errorPopupNode.firmenId)
                        Row("Lizenz", errorPopupNode.lizenz)
                        Row("Proxy", errorPopupNode.proxy ?: "-")
                    }

                    Div({ style { marginTop(4.px); color(DashboardTheme.TEXT_LIGHT); fontWeight("600"); fontSize(12.px) } }) {
                        Text("Letzte Fehler")
                    }

                    Div({
                        style {
                            flexGrow(1)
                            minHeight(0.px)
                            overflowY("auto")
                            display(DisplayStyle.Flex)
                            flexDirection(FlexDirection.Column)
                            gap(6.px)
                        }
                    }) {
                        if (loadingErrors) {
                            Div({ style { color(DashboardTheme.TEXT_MUTED); fontSize(11.px) } }) { Text("Lade Fehlerliste...") }
                        } else if (selectedErrors.isEmpty()) {
                            Div({ style { color(DashboardTheme.TEXT_MUTED); fontSize(11.px) } }) { Text("Keine Fehler gefunden") }
                        } else {
                            selectedErrors.forEach { err ->
                                Div({
                                    style {
                                        padding(8.px)
                                        borderRadius(8.px)
                                        backgroundColor(Color("rgba(255,255,255,0.045)"))
                                        property("border", "1px solid rgba(255,0,0,0.34)")
                                        property("box-shadow", "inset 0 1px 0 rgba(255,255,255,0.08)")
                                    }
                                }) {
                                    Div({ style { color(DashboardTheme.TEXT_LIGHT); fontSize(10.px); fontWeight("700") } }) {
                                        Text("${err.datum} ${err.uhrzeit} | ${err.level}")
                                    }
                                    Div({ style { color(DashboardTheme.TEXT_LIGHT); fontSize(11.px) } }) { Text(err.nachricht) }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun NodeKontrollCard(
    node: SystemNodeInfo,
    selected: Boolean,
    comment: String,
    companyName: String,
    runtimeTickSeconds: Int,
    onSelect: () -> Unit,
    onCommentSave: (String) -> Unit
) {
    var nodeComment by remember(node.id, comment) { mutableStateOf(comment) }
    val activeWorkers = node.workers
        .filter { it.healthy }
        .sortedWith(compareBy<org.example.project.manager.SystemNodeWorkerInfo> { !it.preferred }.thenByDescending { it.buildName })
        .take(2)
    var selectedWorkerBuild by remember(node.id) { mutableStateOf<String?>(null) }
    val selectedWorker = activeWorkers.firstOrNull { it.buildName == selectedWorkerBuild }

    LaunchedEffect(activeWorkers.map { it.buildName }) {
        if (selectedWorkerBuild != null && activeWorkers.none { it.buildName == selectedWorkerBuild }) selectedWorkerBuild = null
    }

    fun saveNodeComment() {
        val normalized = nodeComment.trim()
        if (normalized != comment) onCommentSave(normalized)
    }

    val online = node.status.equals("ONLINE", ignoreCase = true)
    val starting = node.status.equals("STARTING", ignoreCase = true)
    val activeBorderColor = if (selected) UISettings.ACCENT else "rgba(220,230,245,0.24)"
    val activeShadow = if (selected) {
        "0 18px 48px rgba(0,0,0,0.56), 0 0 24px ${UISettings.ACCENT}55, inset 0 1px 0 rgba(255,255,255,0.12)"
    } else {
        "0 14px 34px rgba(0,0,0,0.46), 0 0 18px rgba(235,242,255,0.07), inset 0 1px 0 rgba(255,255,255,0.08)"
    }
    Div({
        classes(TorCardStyles.card)
        style {
            width(360.px)
            property("height", "auto")
            minHeight(365.px)
            padding(10.px)
            gap(12.px)
            alignSelf(AlignSelf.Start)
            property("box-sizing", "border-box")
            property("overflow", "visible")
            property("border", "1px solid $activeBorderColor")
            property("box-shadow", activeShadow)
        }
        onClick { onSelect() }
    }) {
        Div({
            classes(TorCardStyles.header)
            style {
                minHeight(102.px)
                gap(8.px)
                flexDirection(FlexDirection.Column)
                alignItems(AlignItems.Stretch)
            }
        }) {
            Span({
                classes(TorCardStyles.torNumber)
                style {
                    fontSize(24.px)
                    property("-webkit-line-clamp", "1")
                    width(100.percent)
                }
            }) {
                Text(companyName)
            }
            Input(InputType.Text, attrs = {
                classes(TorCardStyles.torTextInput)
                style {
                    height(32.px)
                    width(100.percent)
                    fontSize(13.px)
                    padding(0.px, 9.px)
                }
                value(nodeComment)
                attr("maxlength", "80")
                attr("placeholder", "Node-Notiz")
                onInput { nodeComment = it.value }
                onBlur { saveNodeComment() }
                onKeyDown {
                    if (it.key == "Enter" || it.code == "Enter" || it.code == "NumpadEnter") {
                        it.preventDefault()
                        saveNodeComment()
                    }
                }
            })
        }

        Div({
            style {
                display(DisplayStyle.Flex); alignItems(AlignItems.Center); gap(7.px)
                justifyContent(JustifyContent.Center); width(100.percent)
                height(28.px); padding(0.px, 2.px); fontSize(12.px); fontWeight("700")
            }
        }) {
            Span({
                style {
                    cursor("pointer")
                    property("display", "inline-flex"); alignItems(AlignItems.Center); height(28.px); boxSizing("border-box")
                    color(if (selectedWorkerBuild == null) DashboardTheme.TEXT_LIGHT else DashboardTheme.TEXT_MUTED)
                    property("border-bottom", if (selectedWorkerBuild == null) "2px solid ${UISettings.ACCENT}" else "2px solid transparent")
                }
                onClick { it.stopPropagation(); selectedWorkerBuild = null }
            }) { Text("NODE") }
            if (activeWorkers.isNotEmpty()) Span({ style {
                property("display", "inline-flex"); alignItems(AlignItems.Center); height(28.px); boxSizing("border-box")
                color(DashboardTheme.TEXT_MUTED); property("border-bottom", "2px solid transparent")
            } }) { Text("|") }
            activeWorkers.forEachIndexed { index, worker ->
                val selectedWorkerTab = selectedWorkerBuild == worker.buildName
                Span({
                    style {
                        cursor("pointer")
                        property("display", "inline-flex"); alignItems(AlignItems.Center); height(28.px); boxSizing("border-box")
                        color(if (selectedWorkerTab) DashboardTheme.TEXT_LIGHT else DashboardTheme.TEXT_MUTED)
                        property("border-bottom", if (selectedWorkerTab) "2px solid ${UISettings.ACCENT}" else "2px solid transparent")
                    }
                    onClick { it.stopPropagation(); selectedWorkerBuild = worker.buildName }
                }) { Text(if (index == 0) "WORKER" else "WORKER 2") }
                if (index < activeWorkers.lastIndex) Span({ style {
                    property("display", "inline-flex"); alignItems(AlignItems.Center); height(28.px); boxSizing("border-box")
                    color(DashboardTheme.TEXT_MUTED); property("border-bottom", "2px solid transparent")
                } }) { Text("|") }
            }
        }

        Div({
            classes(TorCardStyles.previewBox)
            style {
                property("height", "auto")
                minHeight(178.px)
                marginTop(0.px)
                borderRadius(12.px)
                property("overflow", "visible")
            }
        }) {
            Div({
                classes(TorCardStyles.previewContent)
                style {
                    position(Position.Static)
                    padding(10.px)
                    gap(6.px)
                    property("height", "auto")
                    property("overflow", "visible")
                }
            }) {
                Div({
                    classes(TorCardStyles.entryItem)
                    style { gap(6.px) }
                }) {
                    Div({
                        classes(TorCardStyles.entryHeadline)
                        style { fontSize(15.px) }
                    }) {
                        Span { Text(if (selectedWorker == null) node.typ.ifBlank { "-" } else "WORKER ${activeWorkers.indexOf(selectedWorker) + 1}") }
                        Span({
                            style {
                                color(
                                    when {
                                        online -> Color("#22c55e")
                                        starting -> Color("#f59e0b")
                                        else -> DashboardTheme.TEXT_MUTED
                                    }
                                )
                            }
                        }) { Text(if (starting) "STARTET" else if (online) "ONLINE" else "OFFLINE") }
                    }
                    Div({
                        classes(TorCardStyles.entryMeta)
                        style {
                            gap(4.px)
                            fontSize(13.px)
                        }
                    }) {
                        if (selectedWorker == null) {
                            NodeMetaRow("Port", node.port.ifBlank { "-" })
                            NodeMetaRow("Umgebung", node.environment.uppercase().ifBlank { "-" })
                            NodeMetaRow("Version", node.version.ifBlank { "-" })
                            NodeMetaRow("Benutzer", node.activeSessions.toString())
                            NodeMetaRow("Start", node.startzeit.ifBlank { "-" })
                            NodeMetaRow("Laufzeit", liveRuntime(node, runtimeTickSeconds))
                            NodeMetaRow("PID", node.processId.ifBlank { "-" })
                            NodeMetaRow("Lizenz", displayNodeLicense(node))
                            NodeMetaRow("Fehler", node.fehlerCounter.toString())
                        } else {
                            NodeMetaRow("Port", node.port.ifBlank { "-" })
                            NodeMetaRow("Umgebung", node.environment.uppercase().ifBlank { "-" })
                            NodeMetaRow("Version", selectedWorker.buildName.removePrefix("build_").ifBlank { "-" })
                            NodeMetaRow("Benutzer", selectedWorker.activeSessions.toString())
                            NodeMetaRow("Start", selectedWorker.startzeit.ifBlank { "-" })
                            NodeMetaRow("Laufzeit", selectedWorker.laufzeit.ifBlank { "-" })
                            NodeMetaRow("PID", node.processId.ifBlank { "-" })
                            NodeMetaRow("Lizenz", displayNodeLicense(node))
                            NodeMetaRow("Fehler", node.fehlerCounter.toString())
                        }
                    }
                }
            }
        }

    }
}

@Composable
private fun NodeWorkerStatus(node: SystemNodeInfo) {
    val activeWorkers = node.workers
        .filter { it.healthy }
        .sortedWith(compareBy<org.example.project.manager.SystemNodeWorkerInfo> { !it.preferred }.thenByDescending { it.buildName })

    Div({
        style {
            marginTop(6.px)
        }
    }) {
        Div({
            style {
                width(64.px)
                height(2.px)
                margin(0.px, 0.px, 10.px, 0.px)
                borderRadius(999.px)
                backgroundColor(Color("rgba(220,230,245,0.34)"))
            }
        })
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                gap(10.px)
                marginBottom(8.px)
                color(DashboardTheme.TEXT_LIGHT)
                fontSize(13.px)
                fontWeight("700")
            }
        }) {
            Span { Text("Aktive Worker: ${activeWorkers.size}") }
            Span { Text("Benutzer gesamt: ${node.activeSessions}") }
        }

        if (activeWorkers.isEmpty()) {
            Div({
                style {
                    padding(10.px)
                    borderRadius(8.px)
                    backgroundColor(Color("rgba(255,255,255,0.035)"))
                    color(DashboardTheme.TEXT_MUTED)
                    fontSize(12.px)
                    textAlign("center")
                }
            }) { Text("Kein aktiver Worker") }
        } else {
            Div({
                style {
                    display(DisplayStyle.Grid)
                    gridTemplateColumns(if (activeWorkers.size == 1) "minmax(0, 1fr)" else "repeat(2, minmax(0, 1fr))")
                    gap(8.px)
                }
            }) {
                activeWorkers.take(2).forEachIndexed { index, worker ->
                    Div({
                        style {
                            minWidth(0.px)
                            padding(2.px, if (index == 0) 8.px else 4.px, 2.px, if (index == 0) 0.px else 12.px)
                            if (index > 0) property("border-left", "1px solid rgba(220,230,245,0.18)")
                            boxSizing("border-box")
                            property("overflow", "hidden")
                        }
                    }) {
                        Div({
                            style {
                                color(DashboardTheme.TEXT_LIGHT)
                                fontSize(13.px)
                                fontWeight("700")
                                property("white-space", "nowrap")
                                property("overflow", "hidden")
                                property("text-overflow", "ellipsis")
                            }
                        }) { Text(worker.buildName.removePrefix("build_").ifBlank { "-" }) }
                        Div({
                            style {
                                marginTop(4.px)
                                color(DashboardTheme.TEXT_MUTED)
                                fontSize(11.px)
                                property("overflow-wrap", "anywhere")
                            }
                        }) { Text("Benutzer: ${worker.activeSessions}") }
                        Div({
                            style {
                                marginTop(4.px)
                                color(DashboardTheme.TEXT_MUTED)
                                fontSize(11.px)
                                property("overflow-wrap", "anywhere")
                            }
                        }) { Text("Start: ${worker.startzeit.ifBlank { "-" }}") }
                        Div({
                            style {
                                marginTop(4.px)
                                color(DashboardTheme.TEXT_MUTED)
                                fontSize(11.px)
                                property("overflow-wrap", "anywhere")
                            }
                        }) { Text("Laufzeit: ${worker.laufzeit.ifBlank { "-" }}") }
                    }
                }
            }
        }
    }
}

@Composable
private fun Row(label: String, value: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.SpaceBetween)
            gap(10.px)
        }
    }) {
        Span({ style { color(DashboardTheme.TEXT_MUTED); fontSize(11.px) } }) { Text(label) }
        Span({
            style {
                color(DashboardTheme.TEXT_LIGHT)
                fontSize(11.px)
                property("text-align", "right")
                property("word-break", "break-word")
            }
        }) { Text(value) }
    }
}

@Composable
private fun ActionButton(label: String, disabled: Boolean, onClickAction: () -> Unit) {
    Div({ style { flexGrow(1) } }) {
        LumaticaButton(
            label = label,
            onClick = { onClickAction() },
            variant = LumaticaButtonVariant.Standard,
            enabled = !disabled
        )
    }
}

private fun typeOrder(typ: String): Int = when (typ.uppercase()) {
    "SYS" -> 0
    "NET" -> 1
    "CUS" -> 2
    else -> 3
}

@Composable
private fun NodeMetaRow(label: String, value: String) {
    Div({ classes(TorCardStyles.metaRow) }) {
        Span { Text(label) }
        Span({
            classes(TorCardStyles.metaValue)
            style {
                fontSize(13.px)
                maxWidth(165.px)
                property("white-space", "nowrap")
                property("overflow", "hidden")
                property("text-overflow", "ellipsis")
            }
        }) { Text(value) }
    }
}

private fun liveRuntime(node: SystemNodeInfo, tickSeconds: Int): String {
    if (!node.status.equals("ONLINE", ignoreCase = true)) {
        return node.laufzeit.ifBlank { "-" }
    }
    val baseSeconds = parseRuntimeToSeconds(node.laufzeit) ?: return node.laufzeit.ifBlank { "-" }
    return formatRuntime(baseSeconds + tickSeconds)
}

private fun parseRuntimeToSeconds(value: String): Int? {
    val trimmed = value
    if (trimmed.isBlank()) return null
    val parts = trimmed.split(":")
    if (parts.size != 3) return null
    val hours = parts[0].toIntOrNull() ?: return null
    val minutes = parts[1].toIntOrNull() ?: return null
    val seconds = parts[2].toIntOrNull() ?: return null
    return hours * 3600 + minutes * 60 + seconds
}

private fun formatRuntime(totalSeconds: Int): String {
    val safe = totalSeconds.coerceAtLeast(0)
    val hours = safe / 3600
    val minutes = (safe % 3600) / 60
    val seconds = safe % 60
    return "${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}"
}

private fun nodeCompanyName(node: SystemNodeInfo, companyNames: Map<String, String>): String =
    if (node.port == "2000") "Netzwerk"
    else node.firma.ifBlank { companyNames[node.firmenId].orEmpty() }.ifBlank { "-" }

private fun displayNodeLicense(node: SystemNodeInfo): String = when (node.firmenId.lowercase()) {
    "ls1000" -> "1000"
    "ls2000" -> "2000"
    else -> node.lizenz.ifBlank { "-" }
}

private fun parseNodeComments(raw: String): Map<String, String> {
    if (raw.isBlank()) return emptyMap()
    return runCatching {
        Json.parseToJsonElement(raw).jsonObject.mapValues { it.value.jsonPrimitive.content }
    }.getOrDefault(emptyMap())
}

private fun nodeCommentsJson(comments: Map<String, String>): String =
    buildJsonObject {
        comments.forEach { (nodeId, comment) ->
            put(nodeId, comment)
        }
    }.toString()
